/**
 * applyRollingRecurrence.js
 *
 * When a recurring task is rescheduled (scheduledStart changes), this utility
 * updates the lead's recurrence.nextJobDate so the Forecast stays accurate.
 *
 * Two modes (stored on lead.recurrence.rollMode):
 *   'schedule'   — next visit anchors off the new scheduled date   (DEFAULT)
 *   'completion' — next visit anchors off actual completion date   (no-op here)
 *
 * The absence of rollMode is treated as 'schedule' to match the default behavior
 * for all existing plans.
 *
 * @param {string}  companyId        — Firestore company doc ID
 * @param {object}  task             — task document { isRecurring, leadId, ... }
 * @param {string}  newScheduledStart — ISO string of the new scheduledStart
 * @param {Array}   leads            — leads array from LeadsContext (for recurrence lookup)
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export async function applyRollingRecurrence(companyId, task, newScheduledStart, leads) {
  // Only applies to recurring tasks with a known lead
  if (!task?.isRecurring || !task?.leadId) return;
  if (!newScheduledStart) return;

  const lead = leads?.find(l => l.id === task.leadId);
  if (!lead?.recurrence?.active) return;

  // Default is 'schedule' — 'completion' explicitly opts out of this logic
  const rollMode = lead.recurrence.rollMode ?? 'schedule';
  if (rollMode === 'completion') return;

  const intervalDays = lead.recurrence?.interval;
  if (!intervalDays || intervalDays <= 0) return;

  const anchor = new Date(newScheduledStart);
  if (isNaN(anchor.getTime())) return;

  const nextDate = new Date(anchor.getTime() + intervalDays * 24 * 60 * 60 * 1000);

  try {
    const leadRef = doc(db, 'companies', companyId, 'leads', task.leadId);
    await updateDoc(leadRef, {
      'recurrence.nextJobDate': nextDate.toISOString(),
    });
    if (process.env.NODE_ENV !== 'production') {
      console.debug(
        `[rolling] nextJobDate → ${nextDate.toISOString().substring(0, 10)} ` +
        `(anchor: ${anchor.toISOString().substring(0, 10)} + ${intervalDays}d) ` +
        `for lead ${task.leadId}`
      );
    }
  } catch (err) {
    // Non-blocking — rolling failure should never block the reschedule itself
    console.warn('[rolling] Failed to update nextJobDate (non-blocking):', err);
  }
}
